import { useT } from "@transifex/react";
import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { ButtonColorVariant } from "../../complex/teacherArea/AreaButton";
import CharacterCount from "../CharacterCount";
import InputWithActions from "../inputWithActions/InputWithActions";
import type { TIconNames } from "../phaseSixIcon/PhaseSixIcon";
import IconTextRow from "../iconTextRow/IconTextRow";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import BasePopupWrapper, { TitleColorVariants } from "./BasePopupWrapper";

interface CreateEditTextInputPopupProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    initialValue: string;
    label: string;
    primaryButton: {
        text: string;
        onSubmit: (value: string) => void | Promise<void>;
        colorVariant?: ButtonColorVariant;
        icon?: TIconNames;
    };
    warningMessage?: string;
    alertMessage?: string;
    onInputChange?: (value: string) => void;
    children?: ReactNode;
    isDisabled?: boolean;
    maxLength?: number;
}

const CreateEditTextInputPopup = (props: CreateEditTextInputPopupProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [inputValue, setInputValue] = useState(props.initialValue);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const savedValueRef = useRef(props.initialValue);

    const t_cancel = t("Cancel", { _tags: "ClassActivity, ClassActivityPopup" });

    useEffect(() => {
        if (props.isOpen) {
            setInputValue(props.initialValue);
            savedValueRef.current = props.initialValue;
            setIsEditing(false);
            props.onInputChange?.(props.initialValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.initialValue]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        props.onInputChange?.(e.target.value);
    };

    const handleSaveEdit = () => {
        if (!inputValue.trim()) {
            setInputValue(savedValueRef.current);
            props.onInputChange?.(savedValueRef.current);
        } else {
            savedValueRef.current = inputValue;
        }
        setIsEditing(false);
    };

    const handleDiscard = () => {
        setInputValue(savedValueRef.current);
        props.onInputChange?.(savedValueRef.current);
        setIsEditing(false);
    };

    const hasWarning = !!props.warningMessage;

    const handleSubmit = async () => {
        if (!inputValue.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await props.primaryButton.onSubmit(inputValue.trim());
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={isSubmitting ? () => {} : props.onClose}
            title={props.title}
            titleColor={TitleColorVariants.DEFAULT}
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
                disabled: isSubmitting,
                colorVariant: ButtonColorVariant.DEFAULT,
                className: "min-w-[180px]",
            }}
            primaryButton={{
                text: props.primaryButton.text,
                onClick: handleSubmit,
                disabled:
                    !inputValue.trim() || hasWarning || !!props.alertMessage || !!props.isDisabled || isSubmitting,
                colorVariant: props.primaryButton.colorVariant ?? ButtonColorVariant.DEFAULT,
                icon: props.primaryButton.icon,
                className: "min-w-[180px]",
            }}
        >
            <div className="flex flex-col gap-s">
                <div className="flex flex-col gap-xs">
                    <div
                        className={`flex ${getStyleBasedOnBreakpoint(
                            Breakpoint.SM,
                            "flex-row flex-wrap items-center gap-xs",
                            "flex-col items-stretch gap-[2px]"
                        )}`}
                    >
                        <span className="text-l text-p6-grey-dark shrink-0">{props.label}:</span>
                        <div
                            data-testid="create-edit-text-input-popup-input"
                            className="flex-1 min-w-0 [&_p]:[overflow-wrap:anywhere]"
                        >
                            <InputWithActions
                                data-testid="create-edit-text-input-popup-input-field"
                                currentText={inputValue}
                                isEditing={isEditing}
                                setIsEditing={setIsEditing}
                                maxLength={props.maxLength}
                                onChange={handleChange}
                                onSave={handleSaveEdit}
                                onDiscard={handleDiscard}
                            />
                        </div>
                    </div>
                    {props.maxLength !== undefined && isEditing && (
                        <CharacterCount
                            currentLength={inputValue.length}
                            maxLength={props.maxLength}
                            dataTestId="create-edit-text-input-popup-character-count"
                        />
                    )}
                    {hasWarning && (
                        <IconTextRow
                            data-testid="create-edit-text-input-popup-warning"
                            icon="alert"
                            text={props.warningMessage!}
                            iconClassName="text-xxl text-p6-red"
                            textClassName="text-l text-p6-red"
                        />
                    )}
                    {!hasWarning && props.alertMessage && (
                        <IconTextRow
                            icon="alert"
                            text={props.alertMessage}
                            iconClassName="text-xxl text-p6-red"
                            textClassName="text-l text-p6-red"
                        />
                    )}
                </div>
                {props.children}
            </div>
        </BasePopupWrapper>
    );
};

export default CreateEditTextInputPopup;
